import { create } from "@bufbuild/protobuf";
import { useMutation, UseMutationOptions } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";

import {
  GetProjectRequestSchema,
  ListProjectsRequestSchema,
} from "@gx/ts-contracts/services/ontology/api/project_pb";
import {
  createProject,
  createProjectSources,
  deleteProject,
  updateProject,
} from "@gx/ts-contracts/services/ontology/api/project-ProjectService_connectquery";

import { DEFAULT_PAGINATION_REQUEST, DEFAULT_SORTING_REQUEST } from "@/api/constants";
import {
  createGetProjectQueryKey,
  createListProjectsInfiniteQueryKey,
} from "@/api/queries/projects";
import { createListUserBookmarksQueryKey } from "@/api/queries/user-bookmarks";

// ========== CREATE PROJECT ==========

export const useCreateProjectMutation = (
  options?: UseMutationOptions<typeof createProject.input, typeof createProject.output>,
) => {
  const queryClient = useQueryClient();
  return useMutation<typeof createProject.input, typeof createProject.output>(createProject, {
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: createListProjectsInfiniteQueryKey(
          create(ListProjectsRequestSchema, {
            pagination: DEFAULT_PAGINATION_REQUEST,
            sorting: DEFAULT_SORTING_REQUEST,
          }),
        ),
      });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

// ========== UPDATE PROJECT ==========

export const useUpdateProjectMutation = (
  options?: UseMutationOptions<typeof updateProject.input, typeof updateProject.output>,
) => {
  const queryClient = useQueryClient();
  return useMutation<typeof updateProject.input, typeof updateProject.output>(updateProject, {
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: createGetProjectQueryKey(
          create(
            GetProjectRequestSchema,
            create(GetProjectRequestSchema, { projectId: variables.projectId }),
          ),
        ),
      });
      void queryClient.invalidateQueries({ queryKey: createListProjectsInfiniteQueryKey() });
      void queryClient.invalidateQueries({ queryKey: createListUserBookmarksQueryKey() });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

// ========== DELETE PROJECT ==========

export const useDeleteProjectMutation = (
  options?: UseMutationOptions<typeof deleteProject.input, typeof deleteProject.output>,
) => {
  const queryClient = useQueryClient();
  return useMutation<typeof deleteProject.input, typeof deleteProject.output>(deleteProject, {
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({ queryKey: createListProjectsInfiniteQueryKey() });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

// ========== CREATE PROJECT SOURCES ==========

export const useCreateProjectSourcesMutation = (
  options?: UseMutationOptions<
    typeof createProjectSources.input,
    typeof createProjectSources.output
  >,
) => {
  return useMutation<typeof createProjectSources.input, typeof createProjectSources.output>(
    createProjectSources,
    options,
  );
};
